import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Select } from 'shared/ui/Select/Select'
import { Button, EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { type IUser, UserCardContent } from 'entities/Users'
import { useEntitiesContext } from 'entities/EntitiesExport'
import type { IFilterValue, IWidgetProps } from 'widgets/FilterEditor/types/types'
import { WidgetMultiUsersTrigger } from './WidgetMultiUsersTrigger'
import styles from './styles.module.scss'

export const WidgetMultiUsers = observer(
  ({
    filterItem,
    expandByDefault = false,
    parentsDropdownId,
  }: IWidgetProps<IFilterValue<number>, string[]>) => {
    const { usersStore } = useEntitiesContext()
    const [showDropdown, setShowDropdown] = useState(false)
    const [showValuesWarning, setShowValuesWarning] = useState(false)
    const filterConfig = filterItem.config
    const payloadValue = (
      Array.isArray(filterItem.payload.value) ? filterItem.payload.value : []
    ).map((id) => Number(id))
    const values = filterConfig.values ?? []
    const [primaryKeys, setPrimaryKeys] = useState<number[]>([])
    const [selectedKeys, setSelectedKeys] = useState(payloadValue)
    const users = values.reduce<IUser[]>((state, current) => {
      const key = current.key
      const user = usersStore.getItem(key)
      if (user) state.push(user)
      return state
    }, [])
    const selectedUsers = users.filter((user) => selectedKeys.includes(user.id))
    const dropdownItems: IDropdownItem[] = useMemo(
      () =>
        users
          .reduce<[IUser[], IUser[]]>(
            (values, value) => {
              const isSelected = primaryKeys.includes(value.id)

              if (isSelected) values[0].push(value)
              else values[1].push(value)

              return values
            },
            [[], []]
          )
          .flat()
          .map((user, index) => {
            return {
              id: user.id,
              label: user.full_name,
              labelContent: <UserCardContent item={user} withoutOnlineStatus={false} />,
              variant: EnumDropdownItemVariant.Checkbox,
              activeValue: selectedKeys.includes(user.id),
              divider: index === primaryKeys.length - 1 && primaryKeys.length !== users.length,
            }
          }),
      [selectedKeys, primaryKeys]
    )

    useEffect(() => {
      setPrimaryKeys(payloadValue)
    }, [])

    useEffect(() => {
      setTimeout(() => setShowDropdown(expandByDefault), 100)
    }, [expandByDefault])

    return (
      <Select
        text={filterItem.operator?.key ?? ''}
        type='multi'
        items={dropdownItems}
        dropdownProps={{
          ariaLabel: 'Select',
          parentsDropdownId: parentsDropdownId,
          show: showDropdown,
          textFieldProps: {
            InputProps: { placeholder: `Search ${filterItem.config.name}` },
          },
          bottomActions: selectedKeys.length
            ? () => (
                <Button
                  text='Clear'
                  contained='secondary'
                  typeBtn='text'
                  onClick={() => {
                    setPrimaryKeys([])
                    setSelectedKeys([])
                    filterItem.payload.updateHard({ value: [] })
                  }}
                />
              )
            : undefined,
          className: styles.operatorDropdown,
          withSearch: true,
          onClose: () => {
            setPrimaryKeys(selectedKeys)
            setShowValuesWarning(!selectedKeys.length)
          },
          triggerComponent: () => (
            <div
              className={classNames(styles.selectTriggerComponent, {
                [styles.warning]: showValuesWarning,
              })}
            >
              <WidgetMultiUsersTrigger
                items={selectedUsers}
                prefix={filterItem.placeholderPrefix}
              />
            </div>
          ),
          onChange: (item) => {
            const id = +item.id
            const keys = item.activeValue
              ? selectedKeys.filter((key) => key !== id)
              : [...selectedKeys, id]

            setShowValuesWarning(false)
            setSelectedKeys(keys)
            filterItem.payload.updateHard({ value: keys.map((key) => String(key)) })
          },
        }}
      />
    )
  }
)
